import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import {
  BaseNavigationItem as NavigationItem,
  IconTypeEnum
} from '@ui/shared/model';

import { TranslatePipe } from '@ngx-translate/core';
import { ImmomioIconButtonComponent } from '@immomio/ui/button';

import { NavigationAlignment } from '../navigation.model';
import {
  collapseXAnimation,
  FadeAnimationStateEnum,
  innerFadeAnimation,
  slideAnimation,
  SlideAnimationStateEnum
} from '../../../../utils/animations';
import { ElevationDirective } from '../../../../directives/elevation/elevation.directive';
import { NavigationService } from '../navigation.service';
import { NavigationDrawerService } from './navigation-drawer.service';
import { NavigationDrawerItemComponent } from './navigation-drawer-item/navigation-drawer-item.component';
import { NavigationDrawerListComponent } from './navigation-drawer-list/navigation-drawer-list.component';

@Component({
  selector: 'app-navigation-drawer',
  templateUrl: './navigation-drawer.component.html',
  styleUrls: ['./navigation-drawer.component.scss'],
  animations: [slideAnimation, innerFadeAnimation, collapseXAnimation],
  providers: [NavigationDrawerService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NavigationDrawerListComponent,
    NavigationDrawerItemComponent,
    ElevationDirective,
    TranslatePipe,
    ImmomioIconButtonComponent
  ]
})
export class NavigationDrawerComponent implements OnInit, OnDestroy {
  navigation = inject(NavigationService);
  navigationDrawer = inject(NavigationDrawerService);

  readonly items = input<NavigationItem[]>(undefined);

  /**
   * Toggleable narrow mode if overall navigation is not an overlay. Will only work as long as there is an icon for each navigation item provided and this menu is not a floating sub menu.
   */
  readonly narrow = input<boolean>(undefined);

  /**
   * Declares this menu as collapsable floating sub menu.
   */
  readonly isFloatingSubMenu = input<boolean>(undefined);

  public readonly widthNarrow = 56;
  public readonly widthWide = 200;

  // Navigation state exposed as signals so the template no longer relies on the
  // async pipe / getters that rebuilt a fresh observable on every change detection.
  protected readonly alignment = toSignal(this.navigation.alignment$);
  protected readonly overlay = toSignal(this.navigation.overlay$);
  protected readonly dark = toSignal(this.navigation.dark$);
  protected readonly mobileView = toSignal(this.navigation.mobileView$);

  protected readonly drawerOpen = toSignal(this.navigationDrawer.open$);
  protected readonly drawerNarrow = toSignal(this.navigationDrawer.narrow$);
  protected readonly floatingSubMenu = toSignal(
    this.navigationDrawer.isFloatingSubMenu$
  );
  protected readonly narrowAndNotMobileView = toSignal(
    this.navigationDrawer.narrowAndNotMobileView$
  );

  protected readonly alignRight = computed(
    () => this.alignment() === NavigationAlignment.RIGHT
  );

  protected readonly slideAnimationState = computed<SlideAnimationStateEnum>(
    () => {
      if (this.drawerOpen()) {
        return SlideAnimationStateEnum.OPEN;
      }
      const left = !this.alignRight();
      if (this.floatingSubMenu()) {
        return left
          ? SlideAnimationStateEnum.CLOSED_LEFT_WITH_OFFSET
          : SlideAnimationStateEnum.CLOSED_RIGHT_WITH_OFFSET;
      }
      return left
        ? SlideAnimationStateEnum.CLOSED_LEFT
        : SlideAnimationStateEnum.CLOSED_RIGHT;
    }
  );

  protected readonly innerFadeState = computed<FadeAnimationStateEnum>(() =>
    this.drawerOpen()
      ? FadeAnimationStateEnum.SHOW
      : FadeAnimationStateEnum.HIDE
  );

  protected readonly collapseState = computed(() =>
    this.narrowAndNotMobileView() === true ? 'closed' : 'open'
  );

  protected readonly collapseWidth = computed(
    () =>
      `${this.narrowAndNotMobileView() ? this.widthNarrow : this.widthWide}px`
  );

  protected readonly elevationLevel = computed(() =>
    this.floatingSubMenu() ? 2 : 0
  );

  // The narrow-toggle rail is only relevant when the drawer can actually collapse
  // (not mobile, not an overlay, not a floating sub menu).
  protected readonly showNarrowToggleContainer = computed(
    () => !this.mobileView() && !this.overlay() && !this.floatingSubMenu()
  );

  protected readonly narrowToggleItem = computed<NavigationItem>(() =>
    this.getNarrowToggleItem(this.alignment(), this.drawerNarrow())
  );

  public ngOnInit() {
    this.navigationDrawer.init({
      isFloatingSubMenu: this.isFloatingSubMenu(),
      narrow: this.narrow()
    });
  }

  public ngOnDestroy() {
    this.navigationDrawer.complete();
  }

  public onCloseClick(): void {
    this.navigation.close();
  }

  public onNarrowToggleClick(narrow: boolean): void {
    if (narrow) {
      this.navigationDrawer.widen();
    } else {
      this.navigationDrawer.tighten();
    }

    this.navigation.setNarrowManualChange(!narrow);
  }

  private getNarrowToggleItem(
    alignment: NavigationAlignment,
    narrow: boolean
  ): NavigationItem {
    const left = alignment === NavigationAlignment.LEFT;
    return narrow
      ? {
          name: 'expand',
          label: 'navigation.expand_sidebar_a',
          icon: left ? IconTypeEnum.ArrowRight : IconTypeEnum.ArrowLeft
        }
      : {
          name: 'collapse',
          label: 'navigation.collapse_sidebar_a',
          icon: left ? IconTypeEnum.ArrowLeft : IconTypeEnum.ArrowRight
        };
  }
}
